import React from 'react';
import { X, Sparkles, Beaker, ChefHat, TrendingUp } from 'lucide-react';

interface WelcomeModalProps {
  onClose: () => void;
  onCreateStarter: () => void;
  onPlanBake: () => void;
}

export function WelcomeModal({ onClose, onCreateStarter, onPlanBake }: WelcomeModalProps) {
  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[200] p-4">
      <div className="bg-gradient-to-br from-[#1C1C1C] to-[#888888] shadow-lg backdrop-blur-xl w-full max-w-2xl max-h-[90vh] flex flex-col text-white">
        <div className="p-4 sm:p-6 md:p-12 border-b border-white/10">
          <div className="flex justify-center items-center">
            <div className="text-center">
              <h2 className="text-3xl sm:text-4xl md:text-6xl text-white font-playfair font-light italic mt-2 sm:mt-6 md:mt-12">
                Welcome to Breadlify
              </h2>
              <p className="text-white/70 text-xs sm:text-sm mt-1 sm:mt-2 mb-3 sm:mb-6 md:mb-12">
                Your journey to perfect sourdough starts here!
              </p>
            </div>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto">
          <div className="p-4 sm:p-6 md:p-8 space-y-3 sm:space-y-4">
              <div className="flex-1 text-center">
                <p className="text-white text-sm sm:text-base md:text-lg font-bold mt-1">
                  The Breadlify App is your quiet baking companion. It helps you understand your dough, your starter, and your timing.
                </p>
                <p className="text-white text-sm sm:text-base md:text-lg mt-4 sm:mt-6 md:mt-8">
                  You don't need to know everything.
                </p>
                <p className="text-white text-sm sm:text-base md:text-lg mb-4 sm:mb-8 md:mb-12">
                  You just need to start where you are.
                </p>
              </div>
            <div className="bg-white/10 border border-white/20 rounded-xl p-3 sm:p-4 md:p-6 hover:bg-white/15 transition-colors">
              <div className="flex items-start gap-3 sm:gap-4">
                <div className="bg-blue-500/20 border border-blue-400/30 rounded-lg p-2 sm:p-3 flex-shrink-0">
                  <Beaker className="w-5 h-5 sm:w-6 sm:h-6 text-blue-300" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-white mb-1 sm:mb-2 text-base sm:text-lg">
                    Step 1: Create Your Starter
                  </h3>
                  <p className="text-white/70 mb-3 sm:mb-4 text-xs sm:text-sm">
                    Start by adding your sourdough starter. Track its health, feeding schedule, and activity.
                  </p>
                  <button
                    onClick={() => {
                      onClose();
                      onCreateStarter();
                    }}
                    className="bg-blue-500/80 hover:bg-blue-500 text-white px-4 sm:px-6 py-2 sm:py-2.5 rounded-xl text-sm sm:text-base font-medium transition-colors"
                  >
                    Create Your First Starter
                  </button>
                </div>
              </div>
            </div>

            <div className="bg-white/10 border border-white/20 rounded-xl p-3 sm:p-4 md:p-6 hover:bg-white/15 transition-colors">
              <div className="flex items-start gap-3 sm:gap-4">
                <div className="bg-amber-500/20 border border-amber-400/30 rounded-lg p-2 sm:p-3 flex-shrink-0">
                  <ChefHat className="w-5 h-5 sm:w-6 sm:h-6 text-amber-300" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-white mb-1 sm:mb-2 text-base sm:text-lg">
                    Step 2: Plan a Baking Session
                  </h3>
                  <p className="text-white/70 mb-3 sm:mb-4 text-xs sm:text-sm">
                    Create your first bake with guided timing for mixing, fermentation, shaping, and baking.
                  </p>
                  <button
                    onClick={() => {
                      onClose();
                      onPlanBake();
                    }}
                    className="bg-amber-500/80 hover:bg-amber-500 text-white px-4 sm:px-6 py-2 sm:py-2.5 rounded-xl text-sm sm:text-base font-medium transition-colors"
                  >
                    Plan Your First Bake
                  </button>
                </div>
              </div>
            </div>

            <div className="bg-white/10 border border-white/20 rounded-xl p-3 sm:p-4 md:p-6 hover:bg-white/15 transition-colors">
              <div className="flex items-start gap-3 sm:gap-4">
                <div className="bg-green-500/20 border border-green-400/30 rounded-lg p-2 sm:p-3 flex-shrink-0">
                  <TrendingUp className="w-5 h-5 sm:w-6 sm:h-6 text-green-300" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-white mb-1 sm:mb-2 text-base sm:text-lg">
                    Step 3: Analyze and Improve
                  </h3>
                  <p className="text-white/70 text-xs sm:text-sm">
                    After each bake, record your results and get personalized feedback to perfect your technique.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="p-4 sm:p-6 border-t border-white/10 text-center">
          <button
            onClick={onClose}
            className="text-white/60 hover:text-white/90 transition-colors text-sm sm:text-base"
          >
            Thanks, I'll explore on my own
          </button>
        </div>
      </div>
    </div>
  );
}
